import capitalize from 'lodash/capitalize';
import partition from 'lodash/partition';

import {reporter} from 'common/report/error-reporting';
import {getProjectId, getWorkspaceId} from 'common/report/util';

import {queryApiUrl} from '../utils';
import {BaseClient} from '../utils/base-client';

class QApiPropertyDefinitionsClient extends BaseClient {
  buildEndpoint() {
    return queryApiUrl(`data_definitions/properties`);
  }

  buildQueryParams(
    options: {
      id?: number;
      name?: string;
      category?: string;
      event?: string;
      includeCustom?: boolean;
      useQueryCache?: boolean;
      includeDensity?: boolean;
      dataGroupId?: string;
    } = {},
  ) {
    const {
      id,
      name,
      category,
      event,
      includeCustom = false,
      useQueryCache = null,
      includeDensity = null,
      dataGroupId = null,
    } = options;
    // "events" is a common mistake
    // the API is case sensitive, but our frontend javascript feeds in lowercase values
    const resourceType = category === `events` ? `Event` : capitalize(category) || undefined;
    const params = {
      id,
      name,
      resourceType,
      event,
      includeCustom,
      project_id: getProjectId(),
      ...(getWorkspaceId() ? {workspace_id: getWorkspaceId()} : {}),
      ...(dataGroupId ? {dataGroupId} : {}),
    };
    if (useQueryCache !== null) {
      params[`use_query_cache`] = useQueryCache;
    }
    if (includeDensity !== null) {
      params[`includeDensity`] = true;
    }
    return params;
  }

  processResponse(response, params) {
    let result = response.results;
    if (response.error) {
      throw new Error(response.error);
    }
    // the API is case sensitive, but our frontend javascript expects lowercase values
    // TODO: fix the inconsistency with `events` and `event` on the param.category
    // use `user` for now
    const resourceType = params.category === `user` ? `user` : `event`;
    if (Array.isArray(result)) {
      result = result.map((result) => {
        return {
          ...result,
          label: result.displayName ?? ``,
          dataGroupId: params.dataGroupId,
        };
      });
      // GOOT for projects with bad user properties see: https://mixpanel.slack.com/archives/C07KZ4NBB/p1605105643087200
      const [valid, invalid] = partition(result, (i) => i.name);
      if (invalid.length > 0) {
        reporter.warn(`unexpected property-definitions for ${resourceType}`, invalid);
      }
      return [...valid.map((def) => ({...def, resourceType}))];
    } else {
      return {...result, resourceType: result.resourceType.toLowerCase()};
    }
  }
}

export const qapiPropertyDefinitionsClient = new QApiPropertyDefinitionsClient();
